import { EMPTY_LIST } from '../../../../constants/common'
import { REQUEST_PROFILE_CHANGE_TYPE } from '../../../../constants/request'
import {
  REQUEST_PROFILE_TAB_ATTACHED_REQUESTS,
  REQUEST_PROFILE_TAB_DOC_FLOWS,
  REQUEST_PROFILE_TAB_DOCUMENTS,
  REQUEST_PROFILE_TAB_EMAILS,
  REQUEST_PROFILE_TAB_POSTS,
} from '../../RequestProfile.constants'

/**
 * The request-profile reload topology: maps a semantic change (what happened)
 * to every profile data source that must refresh.
 *
 * @note
 * Changes with `withListReload` also refresh the host listing (preview mode).
 * `pipeline` skips it because those mutations go through the entity PATCH,
 * which already reloads the listing; `external` IS the listing telling the
 * profile to resync, so notifying back would duplicate the reload.
 *
 * `countersSections` lists the tab counters the change can move (`documents`
 * also counts email, chat and pipeline files, `posts` can span related
 * requests). An empty list reloads every section — for changes where backend
 * automations may touch anything. No `countersSections` = counters untouched.
 */
export function getRequestProfileReloadsByChange({
  onLoadRelatedRequestsCount,
  onLoadRequestProviders,
  onReloadActivitiesTab,
  onReloadDocFlowsTab,
  onReloadDocumentsTab,
  onReloadEmailsTab,
  onReloadEntity,
  onReloadOpenActivities,
  onReloadPostsTab,
  onReloadRating,
  onReloadRelatedRequests,
  onReloadRequestPhases,
}) {
  return {
    [REQUEST_PROFILE_CHANGE_TYPE.activities]: {
      countersSections: EMPTY_LIST,
      reloads: [onReloadEntity, onReloadActivitiesTab, onReloadOpenActivities],
      withListReload: true,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.docFlows]: {
      countersSections: [REQUEST_PROFILE_TAB_DOC_FLOWS],
      reloads: [onReloadEntity, onReloadDocFlowsTab],
      withListReload: true,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.documents]: {
      countersSections: [REQUEST_PROFILE_TAB_DOCUMENTS],
      reloads: [onReloadEntity, onReloadDocumentsTab],
      withListReload: true,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.emails]: {
      countersSections: [
        REQUEST_PROFILE_TAB_EMAILS,
        REQUEST_PROFILE_TAB_DOCUMENTS,
      ],
      reloads: [onReloadEntity, onReloadEmailsTab],
      withListReload: true,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.external]: {
      countersSections: EMPTY_LIST,
      reloads: [
        onReloadEntity,
        onLoadRelatedRequestsCount,
        onLoadRequestProviders,
        onReloadRelatedRequests,
        onReloadRequestPhases,
      ],
      withListReload: false,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.pipeline]: {
      countersSections: EMPTY_LIST,
      reloads: [
        onReloadActivitiesTab,
        onReloadOpenActivities,
        onReloadRequestPhases,
      ],
      withListReload: false,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.posts]: {
      countersSections: [REQUEST_PROFILE_TAB_POSTS],
      reloads: [onReloadEntity, onReloadPostsTab],
      withListReload: true,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.providers]: {
      reloads: [onLoadRequestProviders],
      withListReload: true,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.rating]: {
      countersSections: EMPTY_LIST,
      reloads: [onReloadEntity, onReloadRating],
      withListReload: true,
    },
    [REQUEST_PROFILE_CHANGE_TYPE.relatedRequests]: {
      countersSections: [
        REQUEST_PROFILE_TAB_ATTACHED_REQUESTS,
        REQUEST_PROFILE_TAB_POSTS,
      ],
      reloads: [
        onReloadEntity,
        onLoadRelatedRequestsCount,
        onReloadRelatedRequests,
      ],
      withListReload: true,
    },
  }
}

// `null` = the changes leave the counters alone, `[]` = reload every section
export function getRequestProfileCountersSectionsByChanges({
  changes,
  reloadsByChange,
}) {
  const sectionLists = changes
    .map(change => reloadsByChange[change]?.countersSections)
    .filter(Boolean)

  if (sectionLists.length === 0) {
    return null
  }

  return sectionLists.some(sections => sections.length === 0)
    ? EMPTY_LIST
    : [...new Set(sectionLists.flat())]
}
